import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import verificandoToken from '../../../../FuncoesGerais/VerificarToken';
import { ensureProtocol } from '../../../../config/backend';
import './HardwareFotosCondicaoModal.css';

const MIME_IMG = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp' };
function dataUrlFromBase64(nomeArquivo, base64) {
    if (!base64 || typeof base64 !== 'string') return null;
    const s = base64.trim();
    if (s.startsWith('data:')) return s;
    const match = (nomeArquivo || '').match(/\.([a-z0-9]+)$/i);
    const mime = (match && MIME_IMG[match[1].toLowerCase()]) || 'image/jpeg';
    const idx = s.indexOf('base64,');
    const payload = idx >= 0 ? s.substring(idx + 7).replace(/\s/g, '') : s.replace(/\s/g, '');
    return payload ? `data:${mime};base64,${payload}` : null;
}

export default function HardwareFotosCondicaoModal({ isOpen, onClose, codHar, label, onSaved }) {
    const [fotos, setFotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileName, setFileName] = useState('');
    const [base64, setBase64] = useState('');
    const [msg, setMsg] = useState('');
    const [preview, setPreview] = useState({ open: false, url: null, nome: '' });

    const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2')) + '/hardware';
    const token = () => localStorage.getItem('token');

    const fetchFotos = useCallback(async () => {
        if (!codHar || !isOpen) return;
        setLoading(true);
        setMsg('');
        verificandoToken();
        try {
            const { data } = await axios.get(`${baseUrl}/condicao-fotos`, {
                headers: { Authorization: token() },
                params: { codHar }
            });
            setFotos(Array.isArray(data) ? data : []);
        } catch (e) {
            setMsg(e.response?.data?.error || 'Erro ao carregar fotos.');
            setFotos([]);
        } finally {
            setLoading(false);
        }
    }, [codHar, isOpen, baseUrl]);

    useEffect(() => {
        if (isOpen && codHar) fetchFotos();
    }, [isOpen, codHar, fetchFotos]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const ext = (file.name.match(/\.([a-z0-9]+)$/i) || [])[1]?.toLowerCase();
        if (!MIME_IMG[ext]) {
            setMsg('Selecione uma imagem (jpg, png, gif ou webp).');
            return;
        }
        setMsg('');
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = () => setBase64(reader.result || '');
        reader.onerror = () => setMsg('Erro ao ler o arquivo.');
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!base64 || !fileName.trim()) {
            setMsg('Selecione uma imagem.');
            return;
        }
        setMsg('');
        setUploading(true);
        verificandoToken();
        try {
            await axios.post(`${baseUrl}/condicao-fotos`, {
                codHar,
                nome_arquivo: fileName.trim(),
                conteudo_base64: base64,
                tipo: 'condicao'
            }, { headers: { Authorization: token() } });
            setFileName('');
            setBase64('');
            fetchFotos();
            if (onSaved) onSaved();
        } catch (e) {
            setMsg(e.response?.data?.error || 'Erro ao enviar foto.');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Excluir esta foto?')) return;
        verificandoToken();
        try {
            await axios.delete(`${baseUrl}/condicao-fotos/${id}`, { headers: { Authorization: token() } });
            fetchFotos();
            if (onSaved) onSaved();
        } catch (e) {
            setMsg(e.response?.data?.error || 'Erro ao excluir.');
        }
    };

    const openPreview = (nome, conteudo) => {
        const url = dataUrlFromBase64(nome, conteudo);
        if (url) setPreview({ open: true, url, nome: nome || 'Foto' });
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="hardware-fotos-condicao-backdrop" onClick={onClose} aria-hidden="true" />
            <div className="hardware-fotos-condicao-modal" role="dialog" aria-label="Fotos da condição do equipamento">
                <div className="hardware-fotos-condicao-modal__header">
                    <h3 className="hardware-fotos-condicao-modal__title">Fotos da condição</h3>
                    <button type="button" className="hardware-fotos-condicao-modal__close" onClick={onClose} aria-label="Fechar">
                        <i className="fas fa-times" />
                    </button>
                </div>
                <div className="hardware-fotos-condicao-modal__body">
                    <p className="hardware-fotos-condicao-modal__label">Ativo: <strong>{label || codHar}</strong></p>
                    <p className="hardware-fotos-condicao-modal__hint">Estas fotos podem ser usadas no termo de entrega e devolução.</p>

                    <div className="hardware-fotos-condicao-modal__upload">
                        <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp,image/*" onChange={handleFileChange} className="hardware-fotos-condicao-modal__file" id="hardware-foto-file" />
                        <label htmlFor="hardware-foto-file" className="hardware-fotos-condicao-modal__file-label">
                            <i className="fas fa-camera" /> Escolher imagem
                        </label>
                        {fileName && <span className="hardware-fotos-condicao-modal__file-name">{fileName}</span>}
                        <button type="button" className="hardware-fotos-condicao-modal__btn-upload" onClick={handleUpload} disabled={!base64 || uploading}>
                            {uploading ? 'Enviando...' : 'Enviar foto'}
                        </button>
                    </div>

                    {msg && <div className="hardware-fotos-condicao-modal__msg">{msg}</div>}

                    {loading ? (
                        <div className="hardware-fotos-condicao-modal__loading"><i className="fas fa-spinner fa-spin" /> Carregando...</div>
                    ) : fotos.length === 0 ? (
                        <p className="hardware-fotos-condicao-modal__empty">Nenhuma foto cadastrada ainda.</p>
                    ) : (
                        <ul className="hardware-fotos-condicao-modal__list">
                            {fotos.map((f) => {
                                const url = dataUrlFromBase64(f.nome_arquivo, f.conteudo_base64);
                                const dataStr = f.created_at ? new Date(f.created_at).toLocaleString('pt-BR') : '';
                                return (
                                    <li key={f.id} className="hardware-fotos-condicao-modal__item">
                                        <button type="button" className="hardware-fotos-condicao-modal__thumb-wrap" onClick={() => url && openPreview(f.nome_arquivo, f.conteudo_base64)}>
                                            {url ? <img src={url} alt="" className="hardware-fotos-condicao-modal__thumb" /> : <span className="hardware-fotos-condicao-modal__thumb-placeholder"><i className="fas fa-image" /></span>}
                                        </button>
                                        <div className="hardware-fotos-condicao-modal__item-info">
                                            <span className="hardware-fotos-condicao-modal__item-nome">{f.nome_arquivo || 'Foto'}</span>
                                            {dataStr && <span className="hardware-fotos-condicao-modal__item-date">{dataStr}</span>}
                                        </div>
                                        <button type="button" className="hardware-fotos-condicao-modal__btn-del" onClick={() => handleDelete(f.id)} title="Excluir"><i className="fas fa-trash-alt" /></button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            {preview.open && (
                <div className="hardware-fotos-condicao-preview" onClick={() => setPreview({ open: false, url: null, nome: '' })} role="dialog" aria-label="Visualizar foto">
                    <div className="hardware-fotos-condicao-preview__box" onClick={(e) => e.stopPropagation()}>
                        <div className="hardware-fotos-condicao-preview__header">
                            <span>{preview.nome}</span>
                            <button type="button" onClick={() => setPreview({ open: false, url: null, nome: '' })} aria-label="Fechar">&times;</button>
                        </div>
                        <div className="hardware-fotos-condicao-preview__body">
                            <img src={preview.url} alt={preview.nome} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
